import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import apiService from '../services/apiService';

const MovieRating = ({ movieId, user }) => {
    const [stats, setStats] = useState({ likes: 0, dislikes: 0, total: 0 });
    const [userRating, setUserRating] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Charger les stats au montage du composant
    useEffect(() => {
        if (movieId) {
            fetchRatingStats();
        }
    }, [movieId]);

    /**
     * Récupérer les statistiques de votes du film
     */
    const fetchRatingStats = async () => {
        try {
            const response = await apiService.getMovieRating(movieId);
            if (response.success) {
                setStats(response.data.stats);
                setUserRating(response.data.user_rating);
            }
        } catch (err) {
            console.error('Erreur lors du chargement des stats:', err);
        }
    };

    /**
     * Voter sur le film
     */
    const handleVote = async (rating) => {
        if (!user) {
            setError('Vous devez être connecté pour voter');
            setTimeout(() => setError(null), 3000);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Si l'utilisateur clique sur le même vote, on le retire
            if (userRating === rating) {
                await apiService.unrateMovie(movieId);
                setUserRating(null);
            } else {
                // Sinon on ajoute/met à jour le vote
                await apiService.rateMovie(movieId, rating);
                setUserRating(rating);
            }

            // Recharger les stats
            await fetchRatingStats();
        } catch (err) {
            console.error('Erreur lors du vote:', err);
            setError('Erreur lors de l\'enregistrement du vote');
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center gap-4 py-3">
            {/* Bouton Like */}
            <button
                onClick={() => handleVote('like')}
                disabled={loading}
                className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg font-medium 
                    transition-all duration-200 
                    ${userRating === 'like'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg scale-105'
                    : 'bg-white/10 hover:bg-white/20 text-white/80 hover:text-white'
                }
                    ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                `}
            >
                <ThumbsUp
                    className={`w-5 h-5 ${userRating === 'like' ? 'fill-current' : ''}`}
                />
                <span className="hidden sm:inline">J'aime</span>
                <span className="font-bold">{stats.likes}</span>
            </button>

            {/* Bouton Dislike */}
            <button
                onClick={() => handleVote('dislike')}
                disabled={loading}
                className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg font-medium 
                    transition-all duration-200 
                    ${userRating === 'dislike'
                    ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg scale-105'
                    : 'bg-white/10 hover:bg-white/20 text-white/80 hover:text-white'
                }
                    ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                `}
            >
                <ThumbsDown
                    className={`w-5 h-5 ${userRating === 'dislike' ? 'fill-current' : ''}`}
                />
                <span className="hidden sm:inline">Je n'aime pas</span>
                <span className="font-bold">{stats.dislikes}</span>
            </button>

            {/* Message d'erreur */}
            {error && (
                <div className="text-red-400 text-sm animate-fade-in">
                    {error}
                </div>
            )}
        </div>
    );
};

export default MovieRating;